import { Component, computed, inject, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiComponentsModule } from '../../../../shared/shared-module-ui/ui-components.module';
import { ModalControllerService } from '../../../../shared/shared-services/overlay/modal-controller.service';
import { SharedModalUtils } from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { IStockTake, IStockTakeItemDetail, StockTakeItemStatus } from '../../models';
import { hasPendingBarcodeUpdate } from '../../utils/stock-take-pending-barcode.utils';
import { ISelectOption } from '../../../../shared/shared-models/custom-select/custom-select';
import {
  buildStockTakeReportSortColumnOptions,
  sortStockTakeItemDetailsForReport,
  STOCK_TAKE_CONFIRM_PROCESS_LIST_SORT_COLUMN_ID,
} from '../../utils/stock-take-report-sort.utils';
import {
  calculateStockTakeTotalRandVariance,
  isStockTakeIncludeDelCol,
  stockTakeRandVarianceTotalsDiffer,
} from '../../utils/stock-take-variance.utils';
import { SharedIconsModule } from '../../../../shared-modules/shared-icons/shared-icons.module';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import { StockTakeReportsService } from '../../services/stock-take-reports.service';

/**
 * Confirmation modal shown before processing a stock take on desktop.
 * Displays all counted/unique items with their descriptions and non-zero rand variances.
 * User must explicitly confirm before the stock take is finalised.
 */
@Component({
  selector: 'app-stock-take-confirm-process-modal',
  standalone: true,
  imports: [CommonModule, UiComponentsModule, SharedIconsModule],
  templateUrl: './stock-take-confirm-process-modal.component.html',
  styleUrls: ['./stock-take-confirm-process-modal.component.scss'],
})
export class StockTakeConfirmProcessModalComponent {
  /** All enriched items for the stock take */
  @Input() items: IStockTakeItemDetail[] = [];
  /** Full stock take document used for PDF metadata when printing a pre-process summary */
  @Input() stockTake: IStockTake | null = null;
  /** Name of the stock take, shown in the modal header */
  @Input() stockTakeName: string = '';
  /** When true, copy and actions reflect re-queuing failed backend processing */
  @Input() isRetryMode: boolean = false;
  /** Total rand variance from the grid sub-header before the pre-process refresh */
  @Input() preRefreshTotalRandVariance: number | null = null;

  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly isGeneratingPdf = signal(false);
  protected readonly summarySortColumnId = signal<string>(STOCK_TAKE_CONFIRM_PROCESS_LIST_SORT_COLUMN_ID);

  protected readonly summarySortColumnOptions = computed<Record<string, ISelectOption>>(() =>
    buildStockTakeReportSortColumnOptions(
      isStockTakeIncludeDelCol(this.stockTake?.includeDelCol),
      this.summarySortColumnId(),
    ),
  );

  private readonly modalController = inject(ModalControllerService);
  private readonly reportsService = inject(StockTakeReportsService);
  private readonly sharedModalUtils = inject(SharedModalUtils);

  protected get countedItems(): IStockTakeItemDetail[] {
    return this.items.filter(
      (i: IStockTakeItemDetail) =>
        i.status === StockTakeItemStatus.counted || i.status === StockTakeItemStatus.unique,
    );
  }

  protected get sortedCountedItems(): IStockTakeItemDetail[] {
    return sortStockTakeItemDetailsForReport(this.items, this.summarySortColumnId());
  }

  protected get totalRandVariance(): number {
    return calculateStockTakeTotalRandVariance(this.items);
  }

  protected get hasVarianceTotalChanged(): boolean {
    if (this.isRetryMode || this.preRefreshTotalRandVariance === null) {
      return false;
    }
    return stockTakeRandVarianceTotalsDiffer(this.preRefreshTotalRandVariance, this.totalRandVariance);
  }

  protected get pendingBarcodeUpdateCount(): number {
    return this.countedItems.filter((item: IStockTakeItemDetail) => hasPendingBarcodeUpdate(item)).length;
  }

  protected get binLocationUpdateCount(): number {
    return this.countedItems.filter(
      (item: IStockTakeItemDetail) =>
        !!item.binLocation?.trim() &&
        (item.status === StockTakeItemStatus.counted || item.status === StockTakeItemStatus.unique),
    ).length;
  }

  protected get hasStockFileUpdates(): boolean {
    return this.pendingBarcodeUpdateCount > 0 || this.binLocationUpdateCount > 0;
  }

  protected get canPrintSummary(): boolean {
    return !!this.stockTake && this.countedItems.length > 0;
  }

  protected getRandVarianceValue(item: IStockTakeItemDetail): number {
    return item.randVariance ?? 0;
  }

  /**
   * Update which column drives summary PDF row order and this modal's item list.
   */
  protected onSummarySortColumnChange(selected: Record<string, ISelectOption>): void {
    const columnId = Object.keys(selected).find((key: string) => selected[key].selected);
    if (columnId) {
      this.summarySortColumnId.set(columnId);
    }
  }

  /**
   * Dismiss without processing
   */
  protected onCancel(): void {
    if (this.isGeneratingPdf()) {
      return;
    }
    void this.modalController.dismiss({ confirmed: false });
  }

  /**
   * Confirm and proceed with processing
   */
  protected onConfirm(): void {
    if (this.isGeneratingPdf()) {
      return;
    }
    void this.modalController.dismiss({ confirmed: true });
  }

  /**
   * Print a pre-process draft summary PDF without closing this confirmation modal.
   */
  protected async onPrintSummary(): Promise<void> {
    const stockTake = this.stockTake;
    if (!stockTake || !this.canPrintSummary || this.isGeneratingPdf()) {
      return;
    }

    this.isGeneratingPdf.set(true);
    try {
      await this.reportsService.openPreProcessSummaryPdf(stockTake, this.items, {
        sortColumnId: this.summarySortColumnId(),
      });
    } catch {
      await this.sharedModalUtils.createModal({
        modalTitleIcon: IconsEnum.alertBox,
        modalTitleIconColor: IconColourEnum.red,
        modalTitle: 'PDF generation failed',
        contextDescription: [
          'The pre-process stock take summary PDF could not be generated.',
          'Please try again. If the problem continues, contact Gallix support.',
        ],
        buttonAcceptText: '',
        buttonCloseText: 'OK',
        buttonClose: true,
        buttonAccept: false,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-small'],
      });
    } finally {
      this.isGeneratingPdf.set(false);
    }
  }
}
